import { yupResolver } from "@hookform/resolvers/yup";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { $getSelection } from "lexical";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import yup from "../../../../../utils/yup";
import { useCreateAssetCallback } from "../../../../assets/hooks/use-upsert-asset";
import { useLexicalContext } from "../../lexical-context";
import { $isDesignHuddleNodeMui } from "../../nodes/design-huddle-mui";

import type { FC } from "react";

const saveDHSchema = yup.object({
  url: yup.string().url().required("URL is required"),
  projectId: yup.string().required("Project ID is required"),
  name: yup.string().required("Name is required"),
});

export type SaveDHForm = yup.InferType<typeof saveDHSchema>;

export const SaveDHModalPlugin: FC<{
  disabled: boolean;
}> = () => {
  const createAsset = useCreateAssetCallback();
  const {
    editorModal: { handleClose, open, data },
  } = useLexicalContext();

  const [editor] = useLexicalComposerContext();
  const [saving, setSaving] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
  } = useForm<SaveDHForm>({
    defaultValues: { url: "", name: "", projectId: "" },
    resolver: yupResolver(saveDHSchema),
  });

  useEffect(() => {
    if (data?.type !== "design-huddle") {
      return;
    }

    data.data.url && setValue("url", data.data.url);
    data.data.projectId && setValue("projectId", data.data.projectId);
  }, [data, setValue]);

  const onClose = useCallback(() => {
    reset();
    handleClose();
  }, [reset, handleClose]);

  const onSubmit = useCallback(
    async (values: SaveDHForm) => {
      setSaving(true);

      const { success, asset, error } = await createAsset({
        title: values.name,
        publicUrl: values.url,
        thumbnailUrl: values.url,
        projectId: values.projectId,
        contentType: "Communication",
      });

      if (!success) {
        if (error) {
          setError("name", { type: "custom", message: error.message });
        } else {
          toast.error("There was a problem saving the image");
        }

        setSaving(false);
        return;
      }

      editor.update(() => {
        const selection = $getSelection();
        const node = selection?.getNodes()[0];
        if ($isDesignHuddleNodeMui(node)) {
          node.setSourceAssetId(asset.id);
        }
      });

      toast.success("Image saved successfully");
      setSaving(false);

      onClose();
    },
    [createAsset, editor, onClose, setError],
  );

  if (!open || !data || data.type !== "design-huddle") return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} id="save-image-form">
        <DialogTitle>Save Image to Content Library</DialogTitle>
        <DialogContent>
          <Typography>Save this image to reuse it in your other Communications</Typography>
          <Stack direction="row" alignItems="center" spacing={2} pt={4}>
            <img src={data.data.url} alt="Thumbnail" style={{ width: "100px" }} />
            <Controller
              name="name"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Enter image name"
                  fullWidth
                  error={Boolean(errors.name)}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="lexical-toolbar-cancel-save-to-content-library"
            onClick={onClose}
            color="error"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            data-analytics-id="lexical-toolbar-confirm-save-to-content-library"
            type="submit"
            color="success"
            form="save-image-form"
            loading={saving}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
